import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import styles from './index.module.scss';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';
export type TooltipTrigger = 'hover' | 'click';

/**
 * Props
 * @param {React.ReactNode} children ツールチップを表示するトリガー要素（アイコン・テキストなど）
 * @param {React.ReactNode} content ツールチップ内に表示するコンテンツ
 * @param {'top' | 'bottom' | 'left' | 'right'} [position]
 *  ツールチップの表示位置（デフォルト: top）
 * @param {'hover' | 'click'} [triggerMode]
 *  ツールチップの表示トリガー方法（デフォルト: hover）
 * @param {string} [className]
 *  ラッパー要素に付与される追加クラス名
 */
export interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  position?: TooltipPosition;
  triggerMode?: TooltipTrigger;
  className?: string;
}

export const SliderTooltip: React.FC<TooltipProps> = ({
  children,
  content,
  position = 'top',
  triggerMode = 'hover',
  className,
}) => {
  const [visible, setVisible] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const showTooltip = () => {
    setVisible(true);
  };

  const hideTooltip = () => {
    setVisible(false);
  };

  const toggleTooltip = () => {
    setVisible((prev) => !prev);
  };

  useEffect(() => {
    if (triggerMode !== 'click' || !visible) {
      return () => {};
    }

    const handleOutsideClick = (event: MouseEvent): void => {
      if (
        wrapperRef.current !== null &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setVisible(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [triggerMode, visible]);

  const wrapperClassName = className
    ? `${styles.sliderTooltipWrapper} ${className}`
    : styles.sliderTooltipWrapper;

  const tooltipClassName = `${styles.sliderTooltip} ${styles[position]}`;

  return (
    <div ref={wrapperRef} className={wrapperClassName}>
      <button
        type="button"
        className={styles.trigger}
        aria-describedby="tooltip"
        onMouseEnter={triggerMode === 'hover' ? showTooltip : undefined}
        onMouseLeave={triggerMode === 'hover' ? hideTooltip : undefined}
        onFocus={triggerMode === 'hover' ? showTooltip : undefined}
        onBlur={triggerMode === 'hover' ? hideTooltip : undefined}
        onClick={triggerMode === 'click' ? toggleTooltip : undefined}
      >
        {children}
      </button>

      <div
        id="tooltip"
        role="tooltip"
        className={classnames(tooltipClassName, {
          [styles.active]: visible,
        })}
        onMouseEnter={triggerMode === 'hover' ? showTooltip : undefined}
        onMouseLeave={triggerMode === 'hover' ? hideTooltip : undefined}
      >
        <div className={styles.content}>{content}</div>
        <span className={styles.arrow} />
      </div>
    </div>
  );
};

export default SliderTooltip;
